/**
 * Tip Report PDF generator: matches SplitPreviewDialog structure.
 * Pure generator: returns PDF bytes; IO (logo loading, DB) is done by app layer.
 */
import { getDayBreakdownDisplayRows } from "./tipEngine";
import { TIPPO_PDF_PAGE_SIZE, TIPPO_PDF_GRID, TIPPO_PDF_SPACE, TIPPO_PDF_CARD_METRICS, TIPPO_PDF_TABLE_METRICS, TIPPO_PDF_TYPE, formatUsd, ellipsizeToWidth, clamp, createTippoPdfTheme, getCenteredBaselineY, } from "./tippoPdfKit";
function sumPayoutsCents(payouts) {
    return payouts.reduce((sum, p) => sum + (p.amountCents ?? 0), 0);
}
export async function generateTipReportPdf(options) {
    const { context, day, logoPngBytes } = options;
    const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
    const doc = await PDFDocument.create();
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);
    const logo = await doc.embedPng(logoPngBytes);
    const pageSize = TIPPO_PDF_PAGE_SIZE;
    const grid = TIPPO_PDF_GRID;
    const space = TIPPO_PDF_SPACE;
    const cardMetrics = TIPPO_PDF_CARD_METRICS;
    const tableMetrics = TIPPO_PDF_TABLE_METRICS;
    const type = TIPPO_PDF_TYPE;
    const theme = createTippoPdfTheme(rgb);
    const contentBottomY = grid.marginBottom + grid.footerHeight;
    const maxY = pageSize[1] - grid.marginTop;
    const contentLeftX = grid.marginX;
    const contentRightX = pageSize[0] - grid.marginX;
    const contentWidth = contentRightX - contentLeftX;
    const pages = [];
    let page = doc.addPage(pageSize);
    pages.push({ page });
    let y = maxY;
    const drawFooter = (p, pageIndex, pageCount) => {
        const footerText = context.footerText ?? "Tip Report Produced by Tippo";
        const logoSize = 16;
        const gap = 8;
        const textSize = 9;
        const textWidth = font.widthOfTextAtSize(footerText, textSize);
        const groupWidth = logoSize + gap + textWidth;
        const startX = (pageSize[0] - groupWidth) / 2;
        const baseY = grid.marginBottom - 16;
        p.drawImage(logo, { x: startX, y: baseY, width: logoSize, height: logoSize });
        p.drawText(footerText, {
            x: startX + logoSize + gap,
            y: baseY + 3,
            size: textSize,
            font,
            color: theme.mutedText,
        });
        const pageNumberText = `Page ${pageIndex + 1} of ${pageCount}`;
        const pageNumberSize = 9;
        const pageNumberWidth = font.widthOfTextAtSize(pageNumberText, pageNumberSize);
        p.drawText(pageNumberText, {
            x: contentRightX - pageNumberWidth,
            y: baseY + 3,
            size: pageNumberSize,
            font,
            color: theme.mutedText,
        });
    };
    const addPage = () => {
        page = doc.addPage(pageSize);
        y = maxY;
        pages.push({ page });
    };
    const ensureSpace = (needed) => {
        if (y - needed < contentBottomY)
            addPage();
    };
    const drawText = (options) => {
        const size = options.size ?? type.body.size;
        const line = clamp(size + 4, 10, 28);
        ensureSpace(line + 6);
        const x = options.x ?? contentLeftX;
        const f = options.bold ? fontBold : font;
        const textToDraw = options.maxWidth != null
            ? ellipsizeToWidth({
                text: options.text,
                maxWidth: options.maxWidth,
                font: f,
                size,
            })
            : options.text;
        page.drawText(textToDraw, {
            x,
            y: y + (options.yOffset ?? 0),
            size,
            font: f,
            color: options.color ?? theme.text,
        });
        y -= line;
    };
    const drawRule = (options) => {
        const lineY = options.y ?? y - 6;
        page.drawRectangle({
            x: contentLeftX,
            y: lineY,
            width: contentWidth,
            height: 1,
            color: options.color ?? theme.border,
        });
    };
    const drawKeyValueRow = (options) => {
        const size = options.size ?? type.body.size;
        const line = options.rowHeight ?? clamp(size + 6, 12, 24);
        ensureSpace(line + 2);
        const baselineY = getCenteredBaselineY({ topY: y, rowHeight: line, fontSize: size });
        page.drawText(options.label, {
            x: options.labelX,
            y: baselineY,
            size,
            font: fontBold,
            color: theme.mutedText,
        });
        const valueWidth = font.widthOfTextAtSize(options.value, size);
        page.drawText(options.value, {
            x: options.valueRightX - valueWidth,
            y: baselineY,
            size,
            font: options.emphasizeValue ? fontBold : font,
            color: options.valueColor ?? theme.text,
        });
        y -= line;
    };
    const drawPoolSectionHeader = (title) => {
        // Pool headers need a little more breathing room than generic section headers.
        ensureSpace(type.h2.line + space.xl);
        y -= space.lg;
        page.drawText(title, {
            x: contentLeftX,
            y,
            size: type.h2.size,
            font: fontBold,
            color: theme.accent,
        });
        y -= type.h2.line - 4;
        drawRule({ y: y + space.sm, color: theme.border });
        y -= space.md;
    };
    const drawSectionHeader = (title) => {
        ensureSpace(type.h2.line + space.lg + space.sm);
        y -= space.md;
        page.drawText(title, {
            x: contentLeftX,
            y,
            size: type.h2.size,
            font: fontBold,
            color: theme.accent,
        });
        y -= type.h2.line - 4;
        drawRule({ y: y + space.xs, color: theme.border });
        y -= space.md;
    };
    const drawCard = (options) => {
        ensureSpace(options.height);
        page.drawRectangle({
            x: contentLeftX,
            y: y - options.height,
            width: contentWidth,
            height: options.height,
            color: options.fill ?? theme.surface,
            borderColor: options.border ?? theme.border,
            borderWidth: 1,
        });
    };
    const drawHeaderBand = () => {
        const bandHeight = cardMetrics.headerBandHeight;
        ensureSpace(bandHeight + space.xl);
        page.drawRectangle({
            x: contentLeftX,
            y: y - bandHeight,
            width: contentWidth,
            height: bandHeight,
            color: theme.headerFill,
            borderColor: theme.border,
            borderWidth: 1,
        });
        const logoSize = 40;
        const padX = cardMetrics.paddingX;
        const padY = cardMetrics.paddingY;
        const bandTopY = y;
        const bandBottomY = y - bandHeight;
        const logoY = bandBottomY + (bandHeight - logoSize) / 2;
        page.drawImage(logo, {
            x: contentLeftX + padX,
            y: logoY,
            width: logoSize,
            height: logoSize,
        });
        const textLeft = contentLeftX + padX + logoSize + space.lg;
        const rightPad = cardMetrics.paddingX;
        const titleY = bandTopY - padY - 4;
        page.drawText("Tip report", {
            x: textLeft,
            y: titleY,
            size: type.title.size,
            font: fontBold,
            color: theme.text,
        });
        const metaSize = type.small.size;
        const metaY = titleY - space.sm - metaSize;
        const metaLeft = `${context.restaurantName} • ${context.splitDate}`;
        page.drawText(metaLeft, {
            x: textLeft,
            y: metaY,
            size: metaSize,
            font,
            color: theme.mutedText,
        });
        const metaRight = `v${context.version} • ${context.generatedAt}`;
        const metaRightWidth = font.widthOfTextAtSize(metaRight, metaSize);
        // Align right meta text vertically with left meta for cleaner band symmetry.
        const metaRightY = metaY;
        page.drawText(metaRight, {
            x: contentRightX - rightPad - metaRightWidth,
            y: metaRightY,
            size: metaSize,
            font,
            color: theme.mutedText,
        });
        y = bandBottomY - space.xl;
    };
    const buildRoleColumns = (leftX, totalWidth) => {
        const innerPad = 14;
        const usableWidth = totalWidth - innerPad * 2;
        const roleColWidth = usableWidth * 0.45;
        const hoursColWidth = usableWidth * 0.12;
        const percentColWidth = usableWidth * 0.13;
        const amountColWidth = usableWidth * 0.18;
        const col0X = leftX + innerPad;
        const col1X = col0X + roleColWidth;
        const col2X = col1X + hoursColWidth;
        const col3X = col2X + percentColWidth;
        const cols = [
            { id: "role", header: "Role", x: col0X, width: roleColWidth },
            { id: "hours", header: "Hours", x: col1X, width: hoursColWidth, align: "right" },
            { id: "percent", header: "%", x: col2X, width: percentColWidth, align: "right" },
            { id: "amount", header: "Amount", x: col3X, width: amountColWidth, align: "right" },
        ];
        return cols.slice();
    };
    const buildPayoutColumns = (leftX, totalWidth) => {
        const innerPad = 14;
        const usableWidth = totalWidth - innerPad * 2;
        const staffColWidth = usableWidth * 0.5;
        const roleColWidth = usableWidth * 0.27;
        const amountColWidth = usableWidth * 0.18;
        const col0X = leftX + innerPad;
        const col1X = col0X + staffColWidth;
        const col2X = col1X + roleColWidth;
        const cols = [
            { id: "staff", header: "Staff", x: col0X, width: staffColWidth },
            { id: "role", header: "Role", x: col1X, width: roleColWidth },
            { id: "amount", header: "Amount", x: col2X, width: amountColWidth, align: "right" },
        ];
        return cols.slice();
    };
    const drawTable = (options) => {
        const headerHeight = tableMetrics.headerHeight;
        const rowHeight = options.getRowHeight();
        const cellPadY = tableMetrics.cellPadY;
        const drawHeader = () => {
            ensureSpace(headerHeight + 2);
            // header fill
            const topY = y;
            page.drawRectangle({
                x: contentLeftX,
                y: topY - headerHeight,
                width: contentWidth,
                height: headerHeight,
                color: theme.surfaceMuted,
                borderColor: theme.border,
                borderWidth: tableMetrics.strokeWidth,
            });
            // left/right borders to keep table feeling cohesive
            page.drawRectangle({
                x: contentLeftX,
                y: topY - headerHeight,
                width: tableMetrics.strokeWidth,
                height: headerHeight,
                color: theme.border,
            });
            page.drawRectangle({
                x: contentRightX - tableMetrics.strokeWidth,
                y: topY - headerHeight,
                width: tableMetrics.strokeWidth,
                height: headerHeight,
                color: theme.border,
            });
            for (const col of options.columns) {
                const headerText = ellipsizeToWidth({
                    text: col.header,
                    maxWidth: col.width - 6,
                    font: fontBold,
                    size: type.small.size,
                });
                const headerWidth = fontBold.widthOfTextAtSize(headerText, type.small.size);
                const x = col.align === "right"
                    ? col.x + col.width - headerWidth
                    : col.x;
                const baselineY = getCenteredBaselineY({
                    topY,
                    rowHeight: headerHeight,
                    fontSize: type.small.size,
                });
                page.drawText(headerText, {
                    x,
                    y: baselineY,
                    size: type.small.size,
                    font: fontBold,
                    color: theme.mutedText,
                });
            }
            y -= headerHeight;
        };
        drawHeader();
        for (let i = 0; i < options.rows.length; i++) {
            ensureSpace(rowHeight + 2);
            // If ensureSpace caused a page break, we need to redraw header on the new page.
            if (y === maxY) {
                drawHeader();
                ensureSpace(rowHeight + 2);
            }
            const striped = i % 2 === 1;
            const topY = y;
            if (striped) {
                page.drawRectangle({
                    x: contentLeftX,
                    y: topY - rowHeight,
                    width: contentWidth,
                    height: rowHeight,
                    color: theme.stripeFill,
                });
            }
            // subtle row separators + table side borders for cohesion
            page.drawRectangle({
                x: contentLeftX,
                y: topY - rowHeight,
                width: tableMetrics.strokeWidth,
                height: rowHeight,
                color: theme.border,
            });
            page.drawRectangle({
                x: contentRightX - tableMetrics.strokeWidth,
                y: topY - rowHeight,
                width: tableMetrics.strokeWidth,
                height: rowHeight,
                color: theme.border,
            });
            page.drawRectangle({
                x: contentLeftX,
                y: topY - rowHeight,
                width: contentWidth,
                height: tableMetrics.rowRuleWidth,
                color: theme.border,
            });
            options.drawRow(options.rows[i], i, striped, getCenteredBaselineY({ topY, rowHeight, fontSize: type.small.size }));
            y -= rowHeight;
        }
        y -= space.md;
    };
    drawHeaderBand();
    drawSectionHeader("Day breakdown");
    const formatBreakdownValue = (cents, isDeduction) => {
        if (isDeduction && cents < 0) {
            return `-${formatUsd(Math.abs(cents))}`;
        }
        return formatUsd(cents);
    };
    const breakdownPairs = getDayBreakdownDisplayRows(day.dayBreakdown).map((row) => ({
        label: row.indent ? `  ${row.label}` : row.label,
        value: formatBreakdownValue(row.cents, row.isDeduction),
        emphasize: row.emphasize === true ? true : undefined,
    }));
    const breakdownCardHeight = cardMetrics.paddingY * 2 + breakdownPairs.length * cardMetrics.breakdownRowHeight + space.xs;
    drawCard({ height: breakdownCardHeight, fill: theme.surface, border: theme.border });
    const cardTopY = y;
    const padY = cardMetrics.paddingY;
    const padX = cardMetrics.paddingX;
    const labelX = contentLeftX + padX;
    const valueRightX = contentRightX - padX;
    y = cardTopY - padY;
    for (let index = 0; index < breakdownPairs.length; index++) {
        const pair = breakdownPairs[index];
        const emphasizeValue = pair.emphasize === true ? true : undefined;
        drawKeyValueRow({
            label: pair.label,
            value: pair.value,
            labelX,
            valueRightX,
            size: type.body.size,
            rowHeight: cardMetrics.breakdownRowHeight,
            ...(emphasizeValue ? { emphasizeValue } : {}),
            valueColor: pair.emphasize ? theme.accent : theme.text,
        });
        // subtle grouping divider before manager rows
        const nextPair = breakdownPairs[index + 1];
        if (pair.label === "After deductions" && nextPair?.label === "Manager total") {
            const dividerY = y - space.xs;
            page.drawRectangle({
                x: contentLeftX + padX,
                y: dividerY,
                width: contentWidth - padX * 2,
                height: 0.5,
                color: theme.border,
            });
            y -= space.sm;
        }
    }
    y -= space.lg;
    if (day.managerPayouts.length > 0) {
        drawSectionHeader("Manager payouts");
        const payoutColumnsManager = buildPayoutColumns(contentLeftX, contentWidth);
        const colStaff = payoutColumnsManager[0];
        const colRole = payoutColumnsManager[1];
        const colAmount = payoutColumnsManager[2];
        const rows = day.managerPayouts.map((p) => {
            const staff = day.staffNamesByProfileId[p.staffProfileId] ??
                p.staffProfileId.slice(0, 8) + "…";
            const role = p.roleId != null ? p.roleId.slice(0, 8) + "…" : "Manager";
            return { staff, role, amount: formatUsd(p.amountCents ?? 0) };
        });
        drawTable({
            columns: payoutColumnsManager.slice(),
            rows,
            getRowHeight: () => tableMetrics.rowHeight,
            drawHeader: () => { },
            drawRow: (row, _rowIndex, _striped, baselineY) => {
                const size = type.small.size;
                const staffText = ellipsizeToWidth({
                    text: row.staff,
                    maxWidth: colStaff.width - 6,
                    font,
                    size,
                });
                const roleText = ellipsizeToWidth({
                    text: row.role,
                    maxWidth: colRole.width - 6,
                    font,
                    size,
                });
                page.drawText(staffText, {
                    x: colStaff.x,
                    y: baselineY,
                    size,
                    font,
                    color: theme.text,
                });
                page.drawText(roleText, {
                    x: colRole.x,
                    y: baselineY,
                    size,
                    font,
                    color: theme.mutedText,
                });
                const amtWidth = font.widthOfTextAtSize(row.amount, size);
                page.drawText(row.amount, {
                    x: colAmount.x + colAmount.width - amtWidth,
                    y: baselineY,
                    size,
                    font: fontBold,
                    color: theme.text,
                });
            },
        });
    }
    for (const pool of day.pools) {
        const poolLabel = (pool.poolName ?? pool.poolId).toUpperCase();
        drawPoolSectionHeader(poolLabel);
        // Pool summary card
        const summaryHeight = cardMetrics.summaryHeight;
        drawCard({ height: summaryHeight, fill: theme.surfaceMuted, border: theme.border });
        const summaryTop = y;
        const padX = cardMetrics.paddingX;
        const padY = cardMetrics.paddingY;
        const halfWidth = contentWidth / 2;
        const leftHalfX = contentLeftX;
        const rightHalfX = contentLeftX + halfWidth;
        const centerY = summaryTop - summaryHeight / 2;
        const metricLabelSize = type.tiny.size;
        const metricValueSize = 16;
        const labelY = centerY + metricLabelSize + 6;
        const valueY = centerY - metricValueSize / 2 - 2;
        const poolNetLabel = "Pool net";
        const poolNet = formatUsd(pool.totalTipCents);
        const distributedLabel = "Distributed";
        const distributedValue = formatUsd(sumPayoutsCents(pool.payouts));
        const leftLabelWidth = fontBold.widthOfTextAtSize(poolNetLabel, metricLabelSize);
        const leftValueWidth = fontBold.widthOfTextAtSize(poolNet, metricValueSize);
        page.drawText(poolNetLabel, {
            x: leftHalfX + halfWidth / 2 - leftLabelWidth / 2,
            y: labelY,
            size: metricLabelSize,
            font: fontBold,
            color: theme.mutedText,
        });
        page.drawText(poolNet, {
            x: leftHalfX + halfWidth / 2 - leftValueWidth / 2,
            y: valueY,
            size: metricValueSize,
            font: fontBold,
            color: theme.text,
        });
        const rightLabelWidth = fontBold.widthOfTextAtSize(distributedLabel, metricLabelSize);
        const rightValueWidth = fontBold.widthOfTextAtSize(distributedValue, metricValueSize);
        page.drawText(distributedLabel, {
            x: rightHalfX + halfWidth / 2 - rightLabelWidth / 2,
            y: labelY,
            size: metricLabelSize,
            font: fontBold,
            color: theme.mutedText,
        });
        page.drawText(distributedValue, {
            x: rightHalfX + halfWidth / 2 - rightValueWidth / 2,
            y: valueY,
            size: metricValueSize,
            font: fontBold,
            color: theme.accent,
        });
        // subtle internal vertical divider between left and right halves
        const midX = contentLeftX + contentWidth / 2;
        const dividerTopY = summaryTop - padY;
        const dividerBottomY = summaryTop - summaryHeight + padY;
        page.drawRectangle({
            x: midX,
            y: dividerBottomY,
            width: 0.8,
            height: dividerTopY - dividerBottomY,
            color: theme.border,
        });
        y = summaryTop - summaryHeight - space.md;
        // Role table (keep heading with first table header/row on same page)
        const neededForRoles = type.h3.line + space.sm + tableMetrics.headerHeight + tableMetrics.rowHeight + space.md;
        ensureSpace(neededForRoles);
        y -= space.sm;
        drawText({
            text: "Roles",
            size: type.h3.size,
            bold: true,
            color: theme.accent,
        });
        const roleRows = pool.roles.map((role) => {
            const roleHours = pool.hoursEntries.reduce((sum, e) => (e.roleId === role.id ? sum + e.hours : sum), 0);
            const roleAmountCents = pool.payouts.reduce((sum, p) => (p.roleId === role.id ? sum + p.amountCents : sum), 0);
            const roleName = pool.roleDefinitions.find((r) => r.id === role.id)?.name ??
                role.id.slice(0, 8) + "…";
            return {
                roleName,
                hours: roleHours.toFixed(2),
                percent: `${role.splitPercent.toFixed(2)}%`,
                amount: formatUsd(roleAmountCents),
            };
        });
        const roleColumns = buildRoleColumns(contentLeftX, contentWidth);
        const colRoleName = roleColumns[0];
        const colHours = roleColumns[1];
        const colPercent = roleColumns[2];
        const colRoleAmount = roleColumns[3];
        drawTable({
            columns: roleColumns,
            rows: roleRows,
            getRowHeight: () => tableMetrics.rowHeight,
            drawHeader: () => { },
            drawRow: (r, _rowIndex, _striped, baselineY) => {
                const size = type.small.size;
                const roleText = ellipsizeToWidth({
                    text: r.roleName,
                    maxWidth: colRoleName.width - 6,
                    font,
                    size,
                });
                page.drawText(roleText, {
                    x: colRoleName.x,
                    y: baselineY,
                    size,
                    font,
                    color: theme.text,
                });
                const hoursWidth = font.widthOfTextAtSize(r.hours, size);
                page.drawText(r.hours, {
                    x: colHours.x + colHours.width - hoursWidth,
                    y: baselineY,
                    size,
                    font,
                    color: theme.text,
                });
                const pctWidth = font.widthOfTextAtSize(r.percent, size);
                page.drawText(r.percent, {
                    x: colPercent.x + colPercent.width - pctWidth,
                    y: baselineY,
                    size,
                    font,
                    color: theme.mutedText,
                });
                const amtWidth = font.widthOfTextAtSize(r.amount, size);
                page.drawText(r.amount, {
                    x: colRoleAmount.x + colRoleAmount.width - amtWidth,
                    y: baselineY,
                    size,
                    font: fontBold,
                    color: theme.text,
                });
            },
        });
        // Payouts table (keep heading with table header/first row)
        const neededForPayouts = type.h3.line + space.sm + tableMetrics.headerHeight + tableMetrics.rowHeight + space.md;
        ensureSpace(neededForPayouts);
        y -= space.sm;
        drawText({
            text: "Payouts",
            size: type.h3.size,
            bold: true,
            color: theme.accent,
        });
        const payoutColumns = buildPayoutColumns(contentLeftX, contentWidth);
        const colPayoutStaff = payoutColumns[0];
        const colPayoutRole = payoutColumns[1];
        const colPayoutAmount = payoutColumns[2];
        const payoutRows = pool.payouts.map((p) => {
            const staff = day.staffNamesByProfileId[p.staffProfileId] ??
                p.staffProfileId.slice(0, 8) + "…";
            const role = pool.roleDefinitions.find((r) => r.id === p.roleId)?.name ??
                p.roleId.slice(0, 8) + "…";
            return { staff, role, amount: formatUsd(p.amountCents ?? 0) };
        });
        drawTable({
            columns: payoutColumns,
            rows: payoutRows,
            getRowHeight: () => tableMetrics.rowHeight,
            drawHeader: () => { },
            drawRow: (row, _rowIndex, _striped, baselineY) => {
                const size = type.small.size;
                const staffText = ellipsizeToWidth({
                    text: row.staff,
                    maxWidth: colPayoutStaff.width - 6,
                    font,
                    size,
                });
                const roleText = ellipsizeToWidth({
                    text: row.role,
                    maxWidth: colPayoutRole.width - 6,
                    font,
                    size,
                });
                page.drawText(staffText, {
                    x: colPayoutStaff.x,
                    y: baselineY,
                    size,
                    font,
                    color: theme.text,
                });
                page.drawText(roleText, {
                    x: colPayoutRole.x,
                    y: baselineY,
                    size,
                    font,
                    color: theme.mutedText,
                });
                const amtWidth = font.widthOfTextAtSize(row.amount, size);
                page.drawText(row.amount, {
                    x: colPayoutAmount.x + colPayoutAmount.width - amtWidth,
                    y: baselineY,
                    size,
                    font: fontBold,
                    color: theme.text,
                });
            },
        });
    }
    const pageCount = pages.length;
    for (let i = 0; i < pages.length; i++) {
        drawFooter(pages[i].page, i, pageCount);
    }
    return doc.save();
}
